import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from 'utils/api';
import { logError } from 'utils/datadog';
import { saveGuestFormAnswers } from './api';
import { FormAnswers } from './api-types';
import { isTerminalFormError } from './formErrors';
import { GuestFormSession } from './GuestFormSessionContext';

/** Coalesce a burst of edits — typing into a text field — into one network write. */
const DEBOUNCE_MS = 600;

/** What the UI tells the respondent about their work being kept. */
export type SaveState = 'idle' | 'saving' | 'saved' | 'failed';

interface FormAutosave {
  /** Debounced, fire-and-forget save. Never rejects. */
  scheduleSave: (answers: FormAnswers) => void;
  /** Awaited, latest-wins save used as the submit gate. May reject. */
  flushSave: (answers: FormAnswers) => Promise<void>;
  saveState: SaveState;
}

/**
 * Autosave for the form runner, with the two guarantees this flow needs:
 *
 *  - **Single-flight.** Every new save aborts the one before it, so a slow earlier write can never
 *    land after a newer one and resurrect an answer that was changed.
 *  - **Debounce.** Rapid edits collapse into one request.
 *
 * A 401 means the short session lapsed while the invitation is still valid, so it re-redeems the
 * token still in the URL and retries once. A terminal error — submitted in another tab, invitation
 * revoked, form closed — stops the loop and routes to the screen that explains it, rather than
 * retrying against something that can never accept a write again.
 *
 * `scheduleSave` swallows failures apart from that: a lost autosave is recoverable (the next one
 * carries the same complete answer set), and interrupting someone mid-answer with an error about a
 * background request is worse than the risk. `flushSave` is the submit gate and does propagate,
 * because submitting without the latest answers stored is not recoverable.
 */
export const useFormAutosave = (
  formRef: string,
  sessionToken: string,
  recoverSession: GuestFormSession['recoverSession'],
  routeError: GuestFormSession['routeError']
): FormAutosave => {
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const controllerRef = useRef<AbortController | null>(null);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  // Survives unmount so a late callback can't set state on a gone component.
  const mountedRef = useRef(true);

  const setStateIfMounted = useCallback((next: SaveState) => {
    if (mountedRef.current) {
      setSaveState(next);
    }
  }, []);

  const startRequest = useCallback(() => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    return controller;
  }, []);

  const runSave = useCallback(
    async (answers: FormAnswers) => {
      const controller = startRequest();
      setStateIfMounted('saving');
      try {
        await saveGuestFormAnswers(
          formRef,
          sessionToken,
          answers,
          controller.signal
        );
        setStateIfMounted('saved');
      } catch (error) {
        if (controller.signal.aborted) {
          return; // superseded by a newer save — expected
        }
        if (
          error instanceof ApiError &&
          error.code === 'error.guest.session-expired'
        ) {
          const freshToken = await recoverSession();
          if (!freshToken || controller.signal.aborted) {
            return; // dead invitation: the terminal screen is already selected
          }
          try {
            await saveGuestFormAnswers(
              formRef,
              freshToken,
              answers,
              controller.signal
            );
            setStateIfMounted('saved');
          } catch (retryError) {
            if (controller.signal.aborted) {
              return;
            }
            if (isTerminalFormError(retryError)) {
              routeError(retryError, 'autosave');
              return;
            }
            setStateIfMounted('failed');
            logError('Guest form autosave failed after recovery', retryError);
          }
          return;
        }
        if (isTerminalFormError(error)) {
          routeError(error, 'autosave');
          return;
        }
        setStateIfMounted('failed');
        logError('Guest form autosave failed', error);
      }
    },
    [
      formRef,
      sessionToken,
      recoverSession,
      routeError,
      startRequest,
      setStateIfMounted
    ]
  );

  const scheduleSave = useCallback(
    (answers: FormAnswers) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        void runSave(answers);
      }, DEBOUNCE_MS);
    },
    [runSave]
  );

  const flushSave = useCallback(
    async (answers: FormAnswers) => {
      // Cancel the pending debounce and abort any in-flight autosave, then run this save on its own
      // controller so a concurrent scheduleSave cannot abort it — an aborted submit gate would
      // surface as a spurious failure on the one request that must complete.
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      controllerRef.current?.abort();
      setStateIfMounted('saving');
      try {
        await saveGuestFormAnswers(formRef, sessionToken, answers);
        setStateIfMounted('saved');
      } catch (error) {
        if (
          error instanceof ApiError &&
          error.code === 'error.guest.session-expired'
        ) {
          const freshToken = await recoverSession();
          if (freshToken) {
            await saveGuestFormAnswers(formRef, freshToken, answers);
            setStateIfMounted('saved');
            return;
          }
        }
        setStateIfMounted('failed');
        throw error;
      }
    },
    [formRef, sessionToken, recoverSession, setStateIfMounted]
  );

  useEffect(
    () => () => {
      mountedRef.current = false;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      controllerRef.current?.abort();
    },
    []
  );

  return { scheduleSave, flushSave, saveState };
};
